import React, { useEffect, useState } from 'react';
import { CourseCode, Course } from '../../types';
import { 
  Card, 
  Button, 
  Table, 
  Badge, 
  Form, 
  Modal,
  Row,
  Col,
  Alert,
  InputGroup
} from 'react-bootstrap';
import { 
  FaPlus, 
  FaSearch, 
  FaClipboard,
  FaKey,
  FaDownload
} from 'react-icons/fa';
import { getCourseCodes, updateCourseCodeExpiryDate } from '../../services/courseService';

interface CourseCodesListProps {
  course: Course;
  refreshTrigger?: number;
}

const CourseCodesList: React.FC<CourseCodesListProps> = ({ course, refreshTrigger = 0 }) => {
  const [codes, setCodes] = useState<CourseCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState<CourseCode | null>(null);
  const [newExpiryDays, setNewExpiryDays] = useState(30);

  useEffect(() => {
    loadCodes();
  }, [course.id, refreshTrigger]);

  const loadCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCourseCodes(course.teacherId);
      // فلترة الأكواد حسب الكورس الحالي
      const courseCodes = data.filter(code => code.courseId === course.id);
      setCodes(courseCodes);
    } catch (error) {
      console.error('Error loading course codes:', error);
      setError('حدث خطأ أثناء تحميل أكواد التفعيل');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const handleUpdateExpiry = async () => {
    if (!selectedCode) return;

    try {
      await updateCourseCodeExpiryDate(selectedCode.id, newExpiryDays);
      setShowUpdateModal(false);
      loadCodes();
    } catch (error) {
      console.error('Error updating code expiry:', error);
      setError('حدث خطأ أثناء تحديث تاريخ انتهاء الصلاحية');
    }
  };

  const handleExportCodes = () => {
    const csvContent = [
      ['الكود', 'حالة الاستخدام', 'تاريخ الإنشاء', 'تاريخ الانتهاء', 'المستخدم'],
      ...codes.map(code => [
        code.code,
        code.isUsed ? 'مستخدم' : 'متاح',
        new Date(code.createdAt).toLocaleDateString('ar'),
        code.expiryDate ? new Date(code.expiryDate).toLocaleDateString('ar') : '-',
        code.usedBy || '-'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `course_codes_${course.id}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const filteredCodes = codes.filter(code => 
    code.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-center">جاري التحميل...</div>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <InputGroup style={{ maxWidth: '300px' }}>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="بحث في الأكواد..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </InputGroup>
        <Button variant="outline-secondary" onClick={handleExportCodes}>
          <FaDownload className="me-1" />
          تصدير الأكواد
        </Button>
      </div>

      {filteredCodes.length === 0 ? (
        <Alert variant="info">
          لا توجد أكواد لهذا الكورس
        </Alert>
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th>الكود</th>
              <th>الحالة</th>
              <th>تاريخ الإنشاء</th>
              <th>تاريخ الانتهاء</th>
              <th>المستخدم</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {filteredCodes.map(code => (
              <tr key={code.id}>
                <td>
                  <code>{code.code}</code>
                  <Button
                    variant="link"
                    size="sm"
                    className="ms-2 p-0"
                    onClick={() => handleCopyCode(code.code)}
                  >
                    <FaClipboard />
                  </Button>
                </td>
                <td>
                  <Badge bg={code.isUsed ? 'warning' : (code.isExpired ? 'danger' : 'success')}>
                    {code.isUsed ? 'مستخدم' : (code.isExpired ? 'منتهي' : 'متاح')}
                  </Badge>
                </td>
                <td>{new Date(code.createdAt).toLocaleDateString('ar')}</td>
                <td>
                  {code.expiryDate ? (
                    <>
                      {new Date(code.expiryDate).toLocaleDateString('ar')}
                      {!code.isUsed && (
                        <Button
                          variant="link"
                          size="sm"
                          className="ms-2 p-0"
                          onClick={() => {
                            setSelectedCode(code);
                            setShowUpdateModal(true);
                          }}
                        >
                          <FaKey />
                        </Button>
                      )}
                    </>
                  ) : (
                    '-'
                  )}
                </td>
                <td>{code.usedBy || '-'}</td>
                <td>
                  {/* يمكن إضافة المزيد من الإجراءات هنا */}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>تحديث تاريخ انتهاء الصلاحية</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>مدة الصلاحية الجديدة (بالأيام)</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={365}
              value={newExpiryDays}
              onChange={e => setNewExpiryDays(parseInt(e.target.value))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>
            إلغاء
          </Button>
          <Button variant="primary" onClick={handleUpdateExpiry}>
            تحديث
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseCodesList;
